import { useState, useEffect, useRef } from 'react'
import { Button, IconButton, Box, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { LibraryBooks, TextSnippet } from '@mui/icons-material'
import { isMobile, useMobileOrientation } from 'react-device-detect'
import { checkFile, ConditionalWrapper, trackEvent } from '../Utilities'
import { setFeatureFlag } from '../../api/dataSlice'
import { useGetAiParameterEnabledQuery, getAiParameterId } from '../../api/graphqlApi'
import { useKeycloak } from '../Keycloak'
import { Accordion, AccordionSummary, AccordionDetails } from '../controls/Accordion'
import { setDiaryActions } from '../chatbot/ChatManager'
import dayjs from 'dayjs'
import DatePicker from '../controls/DatePicker'
import CameraSelection from '../controls/CameraSelection'
import PdfDialog from '../controls/PdfDialog'

// gated by MYSITE route access (role check skipped on `protected: false`) and AI parameter `AI_REPORT_ENABLED` (default 7)
export default function DiaryAccordion({ expanded, onChangeExpanded, isPortrait, loading, timestamp, setTimestamp, shouldDisableDate, footerOpen, setFooterOpen, initialCameraId, onCameraIdChange }) {

    const { t } = useTranslation()
    const { CAMERAS, LAYOUT: { TITLE }, BASEURL, ROUTES: { MYSITE } } = window.conf
    const { isPortrait: mobilePortrait } = useMobileOrientation()
    const { hasRouteAccess, isLoggedIn } = useKeycloak()

    const dispatch = useDispatch()
    const hasMysite = hasRouteAccess(MYSITE)

    const { data: diaryAiParameter, isSuccess: diaryAiParameterSuccess, isError: diaryAiParameterError } = useGetAiParameterEnabledQuery(
        { aiParameterId: getAiParameterId('AI_REPORT_ENABLED') },
        { skip: !hasMysite }
    )

    const [cameraId, setCameraId] = useState(initialCameraId ?? (CAMERAS?.length ? CAMERAS[0].id : 0)) // initialCameraId (ArchiveControls) first -> survives mobile-portrait footer unmount
    const [openDialog, setOpenDialog] = useState(false)
    const [fileExists, setFileExists] = useState(false)
    const [alertMessage, setAlertMessage] = useState(null)

    const hasDiary = hasMysite && diaryAiParameter?.enabled
    const diaryCameraIds = diaryAiParameter?.cameraIds ?? []
    const diaryReady = !hasMysite || diaryAiParameterSuccess || diaryAiParameterError
    const moreThanOneCam = CAMERAS.length > 1
    const selectedCam = CAMERAS.find(camera => camera.id === cameraId)
    const selectedDate = timestamp.format("YY-MM-DD")
    const filePath = `${BASEURL}cam${selectedCam.id + 1}/reports/${selectedDate}.pdf`
    const today = dayjs()
    const title = TITLE ? `${TITLE}_` : ""
    const camera = selectedCam.name
        ? `${selectedCam.name}_`
        : moreThanOneCam
            ? `${t("events.camera")} ${selectedCam.id + 1}_`
            : ""
    const targetFilename = `${title}${camera}${selectedDate}` || t("events.construction_diary.construction_diary")
    const stateRef = useRef({})
    stateRef.current = { cameraId, timestamp, fileExists, diaryCameraIds, setTimestamp }

    // chatbot registry — refs feed live state and the latest setTimestamp without stale closures
    useEffect(() => {
        setDiaryActions({
            getSelectedCameraId: () => stateRef.current.cameraId,
            getAvailableCameraIds: () => stateRef.current.diaryCameraIds || [],
            getDate: () => stateRef.current.timestamp?.isValid?.() ? stateRef.current.timestamp.format('YYYY-MM-DD') : null,
            getFileExists: () => !!stateRef.current.fileExists,
            setSelectedCameraId: (id) => setCameraId(id),
            setDate: (dateStr) => {
                if (!dateStr) return
                const date = dayjs(dateStr)
                if (date.isValid() && typeof stateRef.current.setTimestamp === 'function') {
                    stateRef.current.setTimestamp(date)
                }
            },
            openDialog: () => setOpenDialog(true),
        })
        return () => setDiaryActions(null)
    }, [])

    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'hasDiary', value: !!hasDiary }))
    }, [dispatch, hasDiary])

    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'diaryReady', value: !!diaryReady }))
    }, [dispatch, diaryReady])

    // push local selection up so it persists across footer close/reopen
    useEffect(() => {
        onCameraIdChange?.(cameraId)
    // onCameraIdChange is a stable setState from the parent — not in deps on purpose
    }, [cameraId]) // eslint-disable-line react-hooks/exhaustive-deps

    // validate selectedCamera against the AI-enabled set -> restored after a footer close/reopen (or a route flip)
    useEffect(() => {
        if (!hasDiary || diaryCameraIds.length === 0) return
        if (!diaryCameraIds.includes(cameraId)) setCameraId(diaryCameraIds[0])
    // key off the stable joined string so a new-array-same-content render doesn't retrigger
    }, [diaryCameraIds.join(','), hasDiary]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!hasMysite) return // skip the PDF HEAD check for users without the MYSITE role — they can't open the dialog anyway
        let isMounted = true
        const checkPdf = async () => {
            const { exists, contentType } = await checkFile(filePath)

            if (!isMounted) return

            if (exists && contentType === "pdf") {
                setAlertMessage(null)
                setFileExists(true)
            } else {
                if (today.isSame(timestamp, 'day')) {
                    setAlertMessage({ type: 'warning', message: t('events.construction_diary.no_data_today') })
                } else {
                    setAlertMessage({ type: 'error', message: t('msg.no_file') })
                }
                setFileExists(false)
            }
        }
        checkPdf()
        return () => { isMounted = false }
    // `today` is re-created every render but only matters for the "same day" comparison; t is stable — not in deps on purpose
    }, [filePath, timestamp, hasMysite]) // eslint-disable-line react-hooks/exhaustive-deps

    const onOpenDialog = () => {
        setOpenDialog(true)
        trackEvent(t("events.construction_diary.construction_diary", { lng: "de" }), t("actions.create", { lng: "de" }))
    }

    const onOpenDrawer = () => {
        if (setFooterOpen && !footerOpen) {
            setFooterOpen()
        }
    }

    return (
        <>
            <Accordion
                expanded={expanded === 'diary'}
                onChange={onChangeExpanded('diary')}
                isPortrait={isPortrait}
                disabled={!hasDiary}
                disabledTooltip={!isLoggedIn() ? t('msg.sign_in_to_unlock') : undefined}
            >
                <AccordionSummary className="accordion-with-subtitle">
                    <Box>
                        <Typography>{t("events.construction_diary.construction_diary")}</Typography>
                        <Typography className="accordion-subtitle">{`${t("events.construction_diary.analysis")} & ${t("weather.weather")}`}</Typography>
                    </Box>
                </AccordionSummary>

                <AccordionDetails>
                    {isMobile && mobilePortrait && !footerOpen ? (
                        <IconButton
                            aria-label="construction diary"
                            color="primary"
                            onClick={onOpenDrawer}
                            sx={{ pointerEvents: "all" }}
                            disabled={loading || !hasDiary}
                        >
                            <TextSnippet sx={{ fontSize: "2rem" }} />
                        </IconButton>
                    ) : (
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                            {moreThanOneCam && (
                                <CameraSelection
                                    value={cameraId}
                                    onChange={setCameraId}
                                    availableCameras={diaryCameraIds.length ? diaryCameraIds : undefined}
                                />
                            )}

                            <DatePicker
                                timestamp={timestamp}
                                setTimestamp={setTimestamp}
                                loading={loading}
                                shouldDisableDate={shouldDisableDate}
                                pb={2}
                                withoutToday
                            />

                            <ConditionalWrapper
                                condition={timestamp.isSame(today, 'day')}
                                wrapper={children => <Tooltip title={t("events.construction_diary.no_data_today")} placement="top" arrow>{children}</Tooltip>}
                            >
                                <Box component="span">
                                    <Button
                                        onClick={onOpenDialog}
                                        startIcon={<LibraryBooks />}
                                        disabled={loading || !hasDiary || timestamp.isSame(today, 'day')}
                                        variant="contained"
                                        size='large'
                                        fullWidth
                                    >
                                        {t("actions.show")}
                                    </Button>
                                </Box>
                            </ConditionalWrapper>
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>

            <PdfDialog
                open={openDialog}
                setOpen={setOpenDialog}
                alertMessage={alertMessage}
                fileExists={fileExists}
                filePath={filePath}
                targetFilename={targetFilename}
            />
        </>
    )
}
